import { t } from '../i18n.js'
import { Language } from '../types/i18n.js'
import { PlaytimeBadgeArt, PlaytimeProfileData } from '../types/playtime.js'
import { getLevelProgressPercent } from './level-utils.js'

export interface LeaderboardHoverCardData {
    /** e.g. "Tase 7 · 1240 punkti · 23 märki" */
    statsLine: string
    /** 0–100, same formula the LevelProgress bar on this page renders */
    progressPercent: number
    recentBadges: { id: number; name: string; art: PlaytimeBadgeArt }[]
    /** e.g. "+2 märki" for claimed badges beyond the ones shown; null when there are none */
    extraBadgesLabel: string | null
    /** e.g. "Uusim: Mentor · 12.08.2026", null when nothing is claimed */
    newestLine: string | null
}

const RECENT_BADGE_LIMIT = 5

// claimed_date arrives from MariaDB as 'YYYY-MM-DD HH:mm:ss' (dateStrings: true).
// Parse the date-only part at local midnight so formatting doesn't shift the
// day depending on the viewer's timezone offset from UTC.
const formatDate = (isoDate: string, lang: Language): string =>
    new Date(`${isoDate.slice(0, 10)}T00:00:00`).toLocaleDateString(
        lang === 'et' ? 'et-EE' : 'en-GB',
    )

/** Display-ready hover-card content for a leaderboard user's profile payload. */
export const buildLeaderboardHoverCard = (
    profile: PlaytimeProfileData,
    lang: Language,
    scoreKey: 'totalScore' | 'scoreWithoutAnniversary',
): LeaderboardHoverCardData => {
    const claimed = profile.badges
        .filter((b) => b.status === 'claimed')
        .sort((a, b) => (b.claimed_date ?? '').localeCompare(a.claimed_date ?? ''))
    const newest = claimed[0]

    const statsLine = [
        `${t.playtime.levelProgress.level(lang)} ${profile.level}`,
        `${profile[scoreKey]} ${t.playtime.leaderboardView.scoreLabel(lang)}`,
        `${profile.claimedBadgesCount} ${t.playtime.hoverCard.badges(lang)}`,
    ].join(' · ')

    const recentBadges = claimed
        .slice(0, RECENT_BADGE_LIMIT)
        .map((b) => ({ id: b.id, name: b.name, art: b.art }))
    const extraBadgeCount = Math.max(0, profile.claimedBadgesCount - recentBadges.length)

    return {
        statsLine,
        // level/nextThreshold are derived from totalScore server-side regardless
        // of which score column the leaderboard is currently sorted by, so the
        // bar always tracks totalScore — same as LevelProgress does.
        progressPercent: getLevelProgressPercent(profile.totalScore, profile.nextThreshold),
        recentBadges,
        extraBadgesLabel:
            extraBadgeCount > 0 ? `+${extraBadgeCount} ${t.playtime.hoverCard.badges(lang)}` : null,
        newestLine: newest?.claimed_date
            ? `${t.playtime.hoverCard.newest(lang)}: ${newest.name} · ${formatDate(newest.claimed_date, lang)}`
            : null,
    }
}
